import Link from 'next/link';
import { ReactNode } from 'react';

// Import utilities and services
import {
  getLinkType,
  LinkType,
  removeLanguagePrefix,
  cleanBaseUrl,
  cleanMalformedUrl,
  isMailto,
  mapToAppRoute,
  isInternalLink
} from './utils/linkUtils';
import { getBaseUrl } from '../../lib/utils/getBaseUrl';

// Types
interface SmartLinkProps {
  href: string;
  children: ReactNode;
  className?: string;
  title?: string;
  prefetch?: boolean;
  sourceUrl?: string;
  // Accessibility attributes
  'aria-label'?: string;
  'aria-describedby'?: string;
  // HTML attributes
  target?: string;
  rel?: string;
  hrefLang?: string;
  type?: string;
  download?: string | boolean;
  // Additional props
  [key: string]: unknown;
}

export default function SmartLink({
  href,
  children,
  className = '',
  title,
  prefetch = false,
  'aria-label': ariaLabel,
  'aria-describedby': ariaDescribedBy,
  target,
  rel,
  hrefLang,
  type,
  download,
}: SmartLinkProps) {
  // Query-only URLs (e.g. ?popup=...) resolve relative to the current page
  if (href?.startsWith('?')) {
    return (
      <a
        href={href}
        className={className}
        title={title}
        aria-label={ariaLabel}
        aria-describedby={ariaDescribedBy}
        hrefLang={hrefLang}
        type={type}
        download={download}
        rel={rel}
        target={target}
      >
        {children}
      </a>
    );
  }

  // Clean base URL, fix malformed URLs, and remove language prefix
  const cleanedHref = cleanBaseUrl(href || '');
  const fixedUrl = cleanMalformedUrl(cleanedHref);
  const hrefWithoutLanguagePrefix = removeLanguagePrefix(fixedUrl);

  // Mailto links: keep as plain anchors without target manipulation
  if (isMailto(hrefWithoutLanguagePrefix)) {
    return (
      <a
        href={hrefWithoutLanguagePrefix}
        className={className}
        title={title}
        aria-label={ariaLabel}
        aria-describedby={ariaDescribedBy}
        hrefLang={hrefLang}
        type={type}
        download={download}
        rel={rel}
        target={target}
      >
        {children}
      </a>
    );
  }

  // Determine link type
  const linkType = getLinkType(hrefWithoutLanguagePrefix);

  if (linkType === LinkType.AFFILIATE) {
    const affiliateSlug = hrefWithoutLanguagePrefix.replace('/go/', '').replace('/affiliate-link/', '');
    return (
      <a
        href={`/go/${affiliateSlug}`}
        className={className}
        target="_blank"
        rel="sponsored nofollow"
        title={title}
        aria-label={ariaLabel}
        aria-describedby={ariaDescribedBy}
        hrefLang={hrefLang}
        type={type}
        download={download}
      >
        {children}
      </a>
    );
  }

  if (linkType === LinkType.EXTERNAL) {
    return (
      <a
        href={hrefWithoutLanguagePrefix}
        className={className}
        target="_blank"
        rel={`${rel ? rel : ""} ${rel?.includes("noopener") ? "" : "noopener"}`}
        title={title}
        aria-label={ariaLabel}
        aria-describedby={ariaDescribedBy}
        hrefLang={hrefLang}
        type={type}
        download={download}
      >
        {children}
      </a>
    );
  }

  if (isInternalLink(hrefWithoutLanguagePrefix)) {
    const url = new URL(hrefWithoutLanguagePrefix, getBaseUrl());
    const appHref = mapToAppRoute(url.pathname);
    // Preserve query parameters and fragment (hash) if they exist
    const finalHref = `${appHref}${url.search}${url.hash}`;
    return (
      <Link
        href={finalHref}
        className={className}
        title={title}
        target={target || '_self'}
        aria-label={ariaLabel}
        aria-describedby={ariaDescribedBy}
        hrefLang={hrefLang}
        type={type}
        download={download}
        prefetch={prefetch}
      >
        {children}
      </Link>
    );
  }

  return (
    <a
      href={hrefWithoutLanguagePrefix}
      className={className}
      target={target || '_self'}
      rel={rel}
      title={title}
      aria-label={ariaLabel}
      aria-describedby={ariaDescribedBy}
      hrefLang={hrefLang}
      type={type}
      download={download}
    >
      {children}
    </a>
  );
}
